import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { RefreshCw, CheckCircle, AlertCircle, Info, AlertTriangle, Trash2 } from "lucide-react";

const API = "/api";

export default function LogsPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    log_type: "",
    module: "",
  });

  useEffect(() => {
    fetchLogs();
  }, [token, page, filters]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ token, page, limit: 50 });
      if (filters.log_type) params.append("log_type", filters.log_type);
      if (filters.module) params.append("module", filters.module);
      
      const response = await axios.get(`${API}/logs?${params}`);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Loglar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const getLogIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getLogBadge = (type) => {
    switch (type) {
      case "success":
        return "badge-success";
      case "error":
        return "badge-error";
      case "warning":
        return "badge-warning";
      default:
        return "badge-info";
    }
  };

  const logTypes = ["success", "error", "warning", "info"];
  const modules = ["order", "product", "stock", "system"];

  return (
    <div className="space-y-6 fade-in" data-testid="logs-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Log Yönetimi</h1>
          <p className="text-slate-500 mt-1">Sistem loglarını görüntüleyin</p>
        </div>
        <Button variant="outline" onClick={fetchLogs} data-testid="btn-refresh-logs">
          <RefreshCw className="w-4 h-4 mr-2" />
          Yenile
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sistem Logları</CardTitle>
          <CardDescription>Tüm işlem kayıtları</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-6">
            <div className="w-48">
              <Select value={filters.log_type} onValueChange={(v) => setFilters({ ...filters, log_type: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-log-type">
                  <SelectValue placeholder="Log Tipi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {logTypes.map((t) => (
                    <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select value={filters.module} onValueChange={(v) => setFilters({ ...filters, module: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="filter-module">
                  <SelectValue placeholder="Modül" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {modules.map((m) => (
                    <SelectItem key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Logs List */}
          <ScrollArea className="h-[600px]">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-500">
                <Info className="w-8 h-8 mb-2" />
                <p>Henüz log kaydı bulunmuyor</p>
              </div>
            ) : (
              <div className="space-y-3">
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-4 p-4 bg-white border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                    data-testid={`log-entry-${index}`}
                  >
                    {getLogIcon(log.log_type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`text-xs ${getLogBadge(log.log_type)}`}>
                          {log.log_type}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {log.module}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {new Date(log.created_at).toLocaleString("tr-TR")}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 font-medium">{log.message}</p>
                      {log.details && (
                        <p className="text-xs text-slate-500 mt-1 font-mono">{log.details}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <p className="text-sm text-slate-500">
              Toplam {total} log kaydı, Sayfa {page}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
                data-testid="btn-log-prev-page"
              >
                Önceki
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={logs.length < 50}
                onClick={() => setPage((p) => p + 1)}
                data-testid="btn-log-next-page"
              >
                Sonraki
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
